import { normalizeCasing } from '../helpers';

// Shared access to the raw chart rows and the cell formatting used by both the
// visually-hidden agent table (HiddenDataTable) and the visible Table view
// (DataTable). Keeping one source + one formatter guarantees all three tabular
// representations — the .md projection, the hidden table, and the toggled view
// — stay identical.
//
// Rows come from the inline `<script type="application/json" data-chart-for>`
// block Rails injects next to each NvChart div (see PagesHelper
// #inline_chart_data_scripts): the same cached SearchIndexExporter payload that
// backs the .md table. Returns null when the block is absent or empty.
export function readChartRows(collectionName) {
  if (!collectionName) return null;
  const el = document.querySelector(
    `script[type="application/json"][data-chart-for="${CSS.escape(collectionName)}"]`
  );
  if (!el) return null;
  try {
    const parsed = JSON.parse(el.textContent || '[]');
    // Mirror the chart path's display normalization (uppercase precision, §4) so
    // the Table view / CSV match tooltips and never show two casings.
    return Array.isArray(parsed) && parsed.length > 0 ? parsed.map(normalizeCasing) : null;
  } catch {
    return null;
  }
}

// Inject fetched rows as the `data-chart-for` script the rest of the widget
// reads via readChartRows (grouped dropdown, Table, Y-axis availability). On
// the admin preview there is no server-injected payload, so the hybrid fetch
// path seeds it here — once, before the payload-reading children mount — giving
// those controls the same rows the chart renders. No-op if already present or
// there is nothing to write. Not executed by the browser (type is not JS).
export function ensureChartRowsScript(collectionName, rows) {
  if (!collectionName || !Array.isArray(rows) || rows.length === 0) return;
  const selector = `script[type="application/json"][data-chart-for="${CSS.escape(collectionName)}"]`;
  if (document.querySelector(selector)) return;
  const el = document.createElement('script');
  el.type = 'application/json';
  el.setAttribute('data-chart-for', collectionName);
  el.textContent = JSON.stringify(rows);
  document.body.appendChild(el);
}

// The table columns: the union of every row's keys in first-seen order, keeping
// only columns populated in at least one row. Mirrors
// MarkdownTableRenderer.populated_columns so heterogeneous v4 rows (fixed vs
// dynamic carry different fields) don't lose columns present only in later rows,
// and all-null columns are dropped.
export function columnsOf(rows) {
  const ordered = [];
  const seen = new Set();
  (rows || []).forEach(row => {
    if (!row) return;
    Object.keys(row).forEach(key => {
      if (!seen.has(key)) { seen.add(key); ordered.push(key); }
    });
  });
  return ordered.filter(key => (rows || []).some(row => row && row[key] != null));
}

// Characters that make a spreadsheet evaluate a cell as a formula on open.
// Mirrors SearchDataCsvFormatter::FORMULA_TRIGGERS on the server so the
// client-generated CSV (current selection) is as safe as the server export.
const FORMULA_TRIGGERS = ['=', '+', '-', '@', '\t', '\r', '\n'];

// CSV for the current selection (B1). Columns = the same union used by the
// visible table; cells use formatCell so the CSV matches what the user sees,
// with RFC-4180 quoting for commas / quotes / newlines. Returns '' for no rows.
export function rowsToCsv(rows) {
  const columns = columnsOf(rows);
  if (columns.length === 0) return '';
  const escape = (value) => {
    let cell = formatCell(value);
    // Guard only strings — a real number formats without a leading trigger
    // except for negatives, which stay numeric and must not be quoted.
    if (typeof value === 'string' && FORMULA_TRIGGERS.includes(cell[0])) cell = `'${cell}`;
    return /[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
  };
  const lines = [columns.join(',')];
  rows.forEach(row => lines.push(columns.map(col => escape(row?.[col])).join(',')));
  return lines.join('\n');
}

// Mirrors MarkdownTableRenderer.format_cell so a cell reads the same in the UI
// as it does in the .md projection (arrays joined, objects JSON-encoded).
export function formatCell(value) {
  if (value == null) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
